import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Database, Loader2, RefreshCw, Save, RotateCcw, CheckCircle2, XCircle, Eye, EyeOff, BellRing } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { aiApi } from "./aiApi";
import AIAtlasRestore from "./AIAtlasRestore";

const LABEL = { ai_conversations: "Perbualan", ai_messages: "Mesej", ai_actions: "Kad tindakan", ai_files: "Fail", ai_memory: "Memori",
  "ai_files_store.files": "GridFS fail", "ai_files_store.chunks": "GridFS cebisan",
  ai_briefings: "Taklimat", ai_doc_jobs: "Kerja dokumen", ai_image_jobs: "Kerja gambar", ai_job_claims: "Tuntutan jadual",
  ai_job_runs: "Larian jadual", ai_model_compare_jobs: "Banding model", ai_notifications: "Peringatan", ai_quick_prompts: "Arahan pantas",
  ai_scheduled_tasks: "Tugasan berjadual", ai_signatures: "Tandatangan", ai_usage: "Penggunaan", wa_ai_live: "WA langsung",
  wa_ai_sessions: "Sesi WA", wa_inbound: "Mesej WA masuk" };

export default function AIAtlasSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [url, setUrl] = useState("");
  const [dbName, setDbName] = useState("");
  const [copy, setCopy] = useState(true);
  const [show, setShow] = useState(false);
  const load = async () => {
    setBusy("load");
    try { const d = await aiApi.atlas(); setS(d); setDbName(d.db_name || ""); } catch (e) { toast.error(e.response?.data?.detail || "Gagal semak Atlas"); }
    finally { setBusy(""); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!url.trim()) return toast.error("Tampal URL MongoDB Atlas dahulu");
    setBusy("save");
    try {
      const r = await aiApi.saveAtlas({ url: url.trim(), db_name: dbName.trim() || undefined, copy_data: copy });
      const n = Object.values(r.copied || {}).reduce((a, b) => a + b, 0);
      toast.success(`Atlas baharu aktif (${r.latency_ms} ms)${copy ? ` · ${n} dokumen AI disalin` : ""}`);
      setS(r.status); setUrl("");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); } finally { setBusy(""); }
  };
  const testAlert = async (dry) => {
    setBusy("alert");
    try {
      const r = await aiApi.atlasAlertTest(dry);
      const wa = (r.whatsapp || []).map((w) => w.status).join(", ") || "tiada nombor Superadmin";
      toast.success(`Amaran ujian (${r.percent}%) · WhatsApp: ${wa} · Peringatan: ${r.inbox}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal hantar amaran"); } finally { setBusy(""); }
  };
  const reset = async () => {
    setBusy("reset");
    try { setS(await aiApi.resetAtlas()); toast.success("Kembali ke Atlas lalai (.env)"); } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setBusy(""); }
  };

  if (!s) return <div className="rounded-2xl border border-slate-100 bg-white p-3 grid place-items-center h-24"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const st = s.storage;
  const bar = st ? (st.percent >= 90 ? "bg-rose-500" : st.percent >= 75 ? "bg-amber-500" : "bg-emerald-500") : "";
  return (
    <div className="rounded-2xl border border-sky-100 bg-gradient-to-b from-sky-50/60 to-white p-3 space-y-2.5" data-testid="ai-atlas-settings">
      <div className="flex items-center gap-2">
        <Database className="h-4 w-4 text-sky-600" />
        <div className="font-extrabold text-sm text-slate-800 flex-1">MongoDB Atlas (Data AI)</div>
        <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs bg-white" onClick={load} disabled={!!busy} data-testid="ai-atlas-refresh">
          {busy === "load" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>
      <div className="rounded-xl bg-white border border-slate-100 p-2.5 space-y-1 text-[12px]" data-testid="ai-atlas-current">
        <div className="flex items-center gap-1.5 font-semibold" data-testid="ai-atlas-status">
          {s.connected ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-rose-600" />}
          <span className={s.connected ? "text-emerald-700" : "text-rose-700"}>{s.connected ? `Bersambung (${s.latency_ms} ms)` : "Tidak bersambung"}</span>
          <span className="ml-auto text-[10px] font-bold rounded-full px-2 py-0.5 bg-sky-50 border border-sky-200 text-sky-700">{s.source === "manual" ? "Manual" : "Lalai (.env)"}</span>
        </div>
        <div className="text-slate-600 break-all" data-testid="ai-atlas-host">Kluster: <b>{s.host}</b></div>
        <div className="text-slate-600" data-testid="ai-atlas-db">Pangkalan data: <b>{s.db_name}</b></div>
        <div className="text-[10.5px] text-slate-400 break-all">{s.url_masked}</div>
        {s.error && <div className="text-[11px] text-rose-600" data-testid="ai-atlas-error">{s.error}</div>}
      </div>
      {st && (
        <div className="rounded-xl bg-white border border-slate-100 p-2.5 space-y-1.5" data-testid="ai-atlas-storage">
          <div className="flex items-end justify-between text-[12px]">
            <span className="font-bold text-slate-700">Storan kluster</span>
            <span className="text-slate-500"><b className="text-slate-800" data-testid="ai-atlas-used">{st.used_mb} MB</b> / {st.limit_mb} MB ({st.percent}%)</span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${bar}`} style={{ width: `${Math.min(st.percent, 100)}%` }} /></div>
          <div className="text-[13px] font-extrabold text-emerald-700" data-testid="ai-atlas-free">Baki belum digunakan: {st.free_mb} MB</div>
          <div className="space-y-0.5">
            {st.databases.map((d) => (
              <div key={d.name} className="flex justify-between text-[11px] text-slate-500"><span>{d.name}{d.name === s.db_name ? " (data AI)" : ""}</span><span>{d.mb} MB</span></div>
            ))}
          </div>
          {s.ai_collections && (
            <div className="flex flex-wrap gap-1 pt-1" data-testid="ai-atlas-collections">
              {Object.entries(s.ai_collections).map(([k, v]) => (
                <span key={k} className="text-[10.5px] rounded-full bg-slate-50 border border-slate-200 px-2 py-0.5 text-slate-600">{LABEL[k] || k}: <b>{v}</b></span>
              ))}
            </div>
          )}
        </div>
      )}
      {s.alert && (
        <div className={`rounded-xl border p-2.5 space-y-1 ${st && st.percent >= s.alert.threshold ? "bg-rose-50 border-rose-200" : "bg-white border-slate-100"}`} data-testid="ai-atlas-alert">
          <div className="flex items-center gap-1.5 text-[12px] font-bold text-slate-700">
            <BellRing className="h-3.5 w-3.5 text-amber-500" /> Amaran storan WhatsApp
            <span className="ml-auto text-[10.5px] font-semibold text-slate-500" data-testid="ai-atlas-alert-threshold">Ambang {s.alert.threshold}%</span>
          </div>
          <div className="text-[11px] text-slate-500">
            {st && st.percent >= s.alert.threshold ? <b className="text-rose-700">Storan melebihi ambang! Superadmin akan dimaklumkan melalui WhatsApp & Peringatan.</b>
              : "Semakan automatik setiap 6 jam. Superadmin dimaklumkan melalui WhatsApp & tab Peringatan bila storan melebihi ambang (sekali sehari, segera jika naik ke 90%/95%)."}
          </div>
          <div className="text-[10.5px] text-slate-400" data-testid="ai-atlas-alert-last">Amaran terakhir: {s.alert.last_alert_at ? new Date(s.alert.last_alert_at).toLocaleString("ms-MY") : "belum pernah"}</div>
          <div className="flex gap-1.5 pt-0.5">
            <Button size="sm" variant="outline" className="h-7 rounded-lg text-[11px] bg-white" disabled={!!busy} onClick={() => testAlert(true)} data-testid="ai-atlas-alert-test-dry">
              {busy === "alert" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Uji (log sahaja)"}
            </Button>
            <Button size="sm" variant="outline" className="h-7 rounded-lg text-[11px] bg-white text-amber-700 border-amber-200" disabled={!!busy} onClick={() => testAlert(false)} data-testid="ai-atlas-alert-test-send">
              Hantar ujian ke WhatsApp saya
            </Button>
          </div>
        </div>
      )}
      <AIAtlasRestore onDone={load} />
      <div className="space-y-1.5">
        <div className="text-[11px] font-bold text-slate-500">Guna Atlas lain (manual)</div>
        <div className="relative">
          <Input type={show ? "text" : "password"} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="mongodb+srv://user:password@cluster.mongodb.net/?appName=..."
            className="h-8 text-xs bg-white pr-8" autoComplete="off" data-testid="ai-atlas-url" />
          <button type="button" onClick={() => setShow((x) => !x)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" aria-label="Tunjuk URL">
            {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
        <Input value={dbName} onChange={(e) => setDbName(e.target.value)} placeholder="Nama pangkalan data (cth destiny_junior_ai)" className="h-8 text-xs bg-white" data-testid="ai-atlas-dbname" />
        <label className="flex items-center gap-2 text-[11.5px] text-slate-600 cursor-pointer">
          <input type="checkbox" checked={copy} onChange={(e) => setCopy(e.target.checked)} className="accent-sky-600" data-testid="ai-atlas-copy" />
          Salin semua data AI sedia ada (perbualan, fail, memori) ke Atlas baharu
        </label>
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={!!busy} className="flex-1 h-8 rounded-xl text-xs bg-sky-600 hover:bg-sky-700 text-white" data-testid="ai-atlas-save">
            {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Uji & Simpan
          </Button>
          {s.source === "manual" && (
            <Button size="sm" variant="outline" onClick={reset} disabled={!!busy} className="h-8 rounded-xl text-xs bg-white" data-testid="ai-atlas-reset">
              {busy === "reset" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RotateCcw className="h-3.5 w-3.5 mr-1" />Guna lalai</>}
            </Button>
          )}
        </div>
        <div className="text-[10px] text-slate-400">Sambungan diuji dahulu sebelum disimpan. Kata laluan tidak dipaparkan.</div>
      </div>
    </div>
  );
}
